import CompanyOptionLabel from "../CompanyOptionLabel";
// src/components/PopupWindows/LeadDescriptionDialog.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  Box,
  Stack,
  Typography,
  IconButton,
  TextField,
  Button,
  MenuItem,
  Divider,
  Snackbar,
  Alert,
  InputAdornment,
  Autocomplete,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";

import CloseIcon from "@mui/icons-material/Close";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import InlineLinkEntryCard from "./common/InlineLinkEntryCard";
import LocationSearchableField from "../LocationSearchableField";
import { deriveLinkAutofill, describeLinkAutofill, isLinkedInProfileUrl } from "../../utils/linkAutofill";
import {
  createTeamMemberIfMissing,
  fetchTeamRoster,
  normalizeTeamMemberName,
  upsertTeamMemberOption,
} from "../../utils/teamMembers";
import { searchPlatformContacts } from "../../utils/hiringManagers";
import {
  dedupeAutocompleteOptions,
  normalizeAutocompleteText,
} from "../../utils/autocompleteOptions";
import {
  pillButtonBaseSx,
  pillButtonInteractiveSx,
  popupNavigationPillSx,
  withPillButtonClassName,
} from "../../utils/pillButtonStyles";

/**
 * ✅ CRA API base
 * Dev:  REACT_APP_API_BASE=http://localhost:5000/api
 * Prod: REACT_APP_API_BASE=/rpo/api
 */
const API_BASE = process.env.REACT_APP_API_BASE;
const companyFilter = createFilterOptions();
const teamMemberFilter = createFilterOptions();

/* =========================
 * UI styling (match flow mocks)
 * ========================= */
const PURPLE = "#7C3AED";
const PURPLE_HOVER = "#6D28D9";
const SOFT_BG = "#F7F5FF";
const SOFT_BORDER = "#EAE5FF";
const LABEL = "#6B7280";

function getContactNamePart(contact, part) {
  const nameParts = String(contact?.name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return part === "first" ? nameParts[0] || "" : nameParts.slice(1).join(" ");
}

function getContactFullName(contact) {
  return typeof contact === "string" ? contact.trim() : String(contact?.name || "").trim();
}

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: 10, bgcolor: "#fff" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: SOFT_BORDER },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#D7CBFF" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: PURPLE,
    borderWidth: 2,
  },
};

/**
 * ✅ The “form returns / jumps back when typing” is almost always caused by:
 * 1) Defining Card/Footer/ChoiceRow INSIDE the component (new identity each render -> remount -> resets)
 * 2) A useEffect that resets local state when `value` changes, and parent updates `value` on every keystroke.
 *
 * This file fixes BOTH:
 * - Move UI subcomponents OUTSIDE (prevents remount/focus loss)
 * - Only reset state when dialog opens OR when a different lead record is loaded
 *   (not on every keystroke)
 */

function LabelText({ children }) {
  return (
    <Typography data-popup-label sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1, color: LABEL, mb: 0.6 }}>
      {children}
    </Typography>
  );
}

function Section({ children }) {
  return (
    <Typography data-popup-section sx={{ fontSize: 16, fontWeight: 700, lineHeight: "24px", color: PURPLE, letterSpacing: 0.2 }}>
      {children}
    </Typography>
  );
}

function PillButton({ sx, ...props }) {
  return (
    <Button
      {...props}
      className={withPillButtonClassName(props.className)}
      sx={[pillButtonBaseSx, pillButtonInteractiveSx, { fontWeight: 400, px: 2.1 }, sx || {}]}
    />
  );
}

function safeUrl(input) {
  const v = (input || "").trim();
  if (!v) return "";
  try {
    // eslint-disable-next-line no-new
    new URL(v.startsWith("http") ? v : `https://${v}`);
    return v.startsWith("http") ? v : `https://${v}`;
  } catch {
    return null;
  }
}

function getAuthToken() {
  return localStorage.getItem("token") || localStorage.getItem("authToken") || localStorage.getItem("jwt") || "";
}

function getCurrentUserName() {
  try {
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    const name = normalizeTeamMemberName(storedUser?.name || storedUser?.full_name);
    if (name) return name;
  } catch {
    // Fall back to the signed-in user's JWT payload below.
  }

  try {
    const payload = getAuthToken().split(".")[1];
    if (!payload) return "";
    const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return normalizeTeamMemberName(decoded?.name || decoded?.full_name);
  } catch {
    return "";
  }
}

const SOURCE_OPTIONS = ["LinkedIn", "Referral", "Website", "Job board", "Other"];
const STATUS_OPTIONS = [
  "New",
  "Contacted",
  "Discovery",
  "Proposal",
  "Client",
  "Active",
  "Lost",
  "Do not Contact",
  "No stage",
];

function normalizeLookupValue(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function normalizeCompanyName(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function getUrlHostname(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    const normalized = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    return new URL(normalized).hostname.replace(/^www\./i, "").toLowerCase();
  } catch {
    return "";
  }
}

function buildCompanyOption(raw = {}) {
  const company_id = Number(raw?.company_id ?? raw?.companyId ?? raw?.id);
  const company_name = normalizeCompanyName(raw?.company_name ?? raw?.companyName ?? raw?.name ?? "");
  if (!company_name) return null;

  return {
    company_id: Number.isFinite(company_id) && company_id > 0 ? company_id : null,
    company_name,
    company_logo: String(
      raw?.company_logo ?? raw?.company_logo_url ?? raw?.companyLogo ?? raw?.logo ?? ""
    ).trim(),
    website: String(raw?.company_website ?? raw?.companyWebsite ?? raw?.website ?? "").trim(),
  };
}

function getCompanyOptionLabel(option) {
  if (typeof option === "string") return option;
  if (option?.inputValue) return option.inputValue;
  return String(option?.company_name || "");
}

function findMatchingCompanyOption(
  options = [],
  { companyName = "", website = "", strict = false } = {}
) {
  const normalizedName = normalizeLookupValue(companyName);
  const websiteHost = getUrlHostname(website);

  if (websiteHost) {
    const byWebsite = options.find(
      (option) => getUrlHostname(option?.website || "") === websiteHost
    );
    if (byWebsite) return byWebsite;
  }

  if (!normalizedName) return null;

  const exact = options.find(
    (option) => normalizeLookupValue(option?.company_name || "") === normalizedName
  );
  if (exact) return exact;
  if (strict) return null;

  return (
    options.find((option) => {
      const optionName = normalizeLookupValue(option?.company_name || "");
      return optionName && (optionName.includes(normalizedName) || normalizedName.includes(optionName));
    }) || null
  );
}

/* =========================================================
 * ✅ STABLE UI subcomponents (MUST be outside)
 * ======================================================= */
function CardShell({ title, onClose, disabled, children }) {
  return (
    <Box data-popup-surface
      sx={{
        width: "100%",
        border: `1px solid ${SOFT_BORDER}`,
        borderRadius: 3,
        bgcolor: "#fff",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 4, pt: 4, pb: 2 }}>
        <Stack data-popup-header direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: 1.5,
              display: "grid",
              placeItems: "center",
              border: `1px solid ${SOFT_BORDER}`,
              bgcolor: "#fff",
            }}
          >
            <BusinessOutlinedIcon sx={{ fontSize: 16, color: PURPLE }} />
          </Box>

          <Typography data-popup-title sx={{ fontSize: 20, fontWeight: 400, lineHeight: "35px", color: "#111827" }}>
            {title}
          </Typography>

          <Box sx={{ flex: 1 }} />

          <IconButton data-popup-close
            onClick={onClose}
            disabled={disabled}
            sx={{
              width: 30,
              height: 30,
              borderRadius: 999,
              border: `1px solid ${SOFT_BORDER}`,
              bgcolor: "#fff",
            }}
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Box>

      <Divider sx={{ borderColor: SOFT_BORDER }} />
      {children}
    </Box>
  );
}

function FooterBar({
  disabled,
  leftText,
  onLeft,
  rightText,
  onRight,
  rightVariant = "contained",
  centerLeftText,
  onCenterLeft,
  centerRightText,
  onCenterRight,
}) {
  const leftIsBack = String(leftText || "").trim().toLowerCase() === "back";
  const rightIsNavigation = ["next", "continue"].includes(
    String(rightText || "").trim().toLowerCase()
  );
  const footerScrollSx = {
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
  };
  const footerRowSx = {
    width: "max-content",
    minWidth: "100%",
    flexWrap: "nowrap",
  };
  const footerButtonSx = {
    flexShrink: 0,
    whiteSpace: "nowrap",
  };

  return (
    <Box data-popup-footer sx={{ px: 4, pb: 4, pt: 2, ...footerScrollSx }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={footerRowSx}>
        <PillButton
          variant={leftIsBack ? "contained" : "outlined"}
          onClick={onLeft}
          disabled={disabled}
          startIcon={leftIsBack ? <ArrowBackRoundedIcon /> : null}
          sx={{
            ...footerButtonSx,
            borderColor: leftIsBack ? PURPLE : SOFT_BORDER,
            color: leftIsBack ? "#fff" : PURPLE,
            bgcolor: leftIsBack ? PURPLE : "#fff",
            "&:hover": {
              bgcolor: leftIsBack ? PURPLE_HOVER : "#fff",
            },
            ...(leftIsBack ? popupNavigationPillSx : {}),
          }}
        >
          {leftText}
        </PillButton>

        <Box sx={{ flex: 1 }} />

        {!!centerLeftText && (
          <PillButton
            variant="outlined"
            onClick={onCenterLeft}
            disabled={disabled}
            sx={{
              ...footerButtonSx,
              borderColor: SOFT_BORDER,
              color: PURPLE,
              bgcolor: "#fff",
            }}
          >
            {centerLeftText}
          </PillButton>
        )}

        {!!centerRightText && (
          <PillButton
            variant="outlined"
            onClick={onCenterRight}
            disabled={disabled}
            sx={{
              ...footerButtonSx,
              borderColor: SOFT_BORDER,
              color: PURPLE,
              bgcolor: "#fff",
            }}
          >
            {centerRightText}
          </PillButton>
        )}

        <PillButton
          variant={rightVariant}
          onClick={onRight}
          disabled={disabled}
          endIcon={rightIsNavigation ? <ArrowForwardRoundedIcon /> : null}
          sx={{
            ...footerButtonSx,
            bgcolor: rightVariant === "contained" ? PURPLE : "#fff",
            color: rightVariant === "contained" ? "#fff" : PURPLE,
            borderColor: SOFT_BORDER,
            "&:hover": { bgcolor: rightVariant === "contained" ? PURPLE_HOVER : "#fff" },
            minWidth: 110,
            ...(rightIsNavigation ? popupNavigationPillSx : {}),
          }}
        >
          {rightText}
        </PillButton>
      </Stack>
    </Box>
  );
}

function ChoiceRow({ icon, title, subtitle, onClick }) {
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      sx={{
        border: `1px solid ${SOFT_BORDER}`,
        borderRadius: 2.5,
        bgcolor: "#fff",
        p: 1.5,
        cursor: "pointer",
        "&:hover": { boxShadow: "0 10px 28px rgba(124,58,237,0.12)" },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: SOFT_BG,
            border: `1px solid ${SOFT_BORDER}`,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "#111827" }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "#6B7280", mt: 0.25 }}>
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

/* =========================
 * Main component
 * ========================= */
export default function LeadDescriptionDialog({
  open,
  onClose,
  value,
  onChange,
  initialScreen = "start",
  mode = "lead",
  readOnly = false,
  titleOverride,
  submitLabel,
  // kept for backward compatibility
  onSubmit,
  apiBase = API_BASE,
}) {
  const navigate = useNavigate();
  const requestedContactMode = String(mode || "lead").toLowerCase() === "contact";
  const [contactFromProfileLink, setContactFromProfileLink] = useState(false);
  const isContactMode = requestedContactMode || contactFromProfileLink;
  const entityLabel = isContactMode ? "Contact" : "Lead";

  const defaults = useMemo(
    () => ({
      companyName: "",
      companyId: null,
      companyLogo: "",
      website: "",
      source: "LinkedIn",
      firstName: "",
      contactTitle: "",
      jobId: null,
      lastName: "",
      email: "",
      phoneNumber: "",
      jobTitle: "",
      jobOpenings: "",
      location: "",

      leadLink: "",
      linkedin: "",
      otherLink: "",
      notes: "",
      teamMember: "",
      leadStatus: "New",

      // optional stable identifier if parent provides it
      id: null,
      lead_id: null,
    }),
    []
  );

  // start -> link -> overview -> additional
  const [screen, setScreen] = useState("start");
  const [local, setLocal] = useState({ ...defaults, ...(value || {}) });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [companyLookupError, setCompanyLookupError] = useState("");
  const [companyOption, setCompanyOption] = useState(null);
  const [companyInputValue, setCompanyInputValue] = useState("");
  const [companyJobs, setCompanyJobs] = useState([]);
  const [loadingCompanyJobs, setLoadingCompanyJobs] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [teamMembersDebug, setTeamMembersDebug] = useState("");
  const [platformContactOptions, setPlatformContactOptions] = useState([]);
  const [loadingPlatformContacts, setLoadingPlatformContacts] = useState(false);

  const fileInputRef = useRef(null);
  const editingLeadId = Number(local?.id ?? local?.lead_id ?? 0);
  const isEditingExisting = !isContactMode && Number.isFinite(editingLeadId) && editingLeadId > 0;
  const baseDialogTitle =
    titleOverride || `${isEditingExisting ? "Edit" : "Add"} ${entityLabel}`;
  const dialogSubmitText =
    submitLabel || `${isEditingExisting ? "Update" : "Add"} ${entityLabel}`;

  // ✅ Track last loaded record (prevents resetting while typing)
  const lastLoadedKeyRef = useRef("");

  const makeLoadKey = (v) => {
    // Prefer a real id if provided; fall back to a stable signature only when opening.
    const id = v?.id ?? v?.lead_id ?? "";
    if (id) return `id:${id}`;
    // fallback signature (best effort): company+email+jobTitle
    return `sig:${String(v?.companyName || "")}|${String(v?.email || "")}|${String(v?.jobTitle || "")}`;
  };

  // ✅ ONLY reset when dialog opens OR when a DIFFERENT lead is provided
  useEffect(() => {
    if (!open) return;

    const incoming = { ...(value || {}) };
    // Quick actions commonly provide `company`, while the form/API use
    // `companyName`. Normalize both so a preselected company is not lost
    // between the overview and additional-details screens.
    incoming.companyName =
      incoming.companyName ?? incoming.company_name ?? incoming.company ?? "";
    incoming.companyId = incoming.companyId ?? incoming.company_id ?? null;
    incoming.companyLogo = incoming.companyLogo ?? incoming.company_logo ?? incoming.logo ?? "";
    incoming.website = incoming.website ?? incoming.company_website ?? incoming.companyWebsite ?? "";
    incoming.teamMember =
      incoming.teamMember ?? incoming.team_member ?? incoming.assigned_team_member ?? "";
    incoming.contactTitle = incoming.contactTitle ?? incoming.jobTitle ?? "";

    // Backward-compat helpers
    if (incoming.location && !incoming.city) incoming.city = incoming.location;
    if (incoming.city && !incoming.location) incoming.location = incoming.city;

    const incomingKey = makeLoadKey(incoming);

    // If we already loaded this same record while open, don't reset (prevents jump-back on typing)
    if (lastLoadedKeyRef.current === incomingKey) return;

    lastLoadedKeyRef.current = incomingKey;
    setLocal({ ...defaults, ...incoming });
    setErrorMsg("");
    setFieldErrors({});
    setSubmitting(false);
    setContactFromProfileLink(false);
    const nextInitialScreen =
      initialScreen && (!requestedContactMode || initialScreen !== "start")
        ? initialScreen
        : requestedContactMode
          ? "overview"
          : "start";

    setScreen(nextInitialScreen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialScreen, requestedContactMode]); // intentionally NOT depending on `value`

  // ✅ When dialog closes, clear the last key so next open loads fresh
  useEffect(() => {
    if (open) return;
    lastLoadedKeyRef.current = "";
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const loadCompanies = async () => {
      setLoadingCompanies(true);
      setCompanyLookupError("");

      try {
        const token = getAuthToken();
        const response = await fetch(`${apiBase}/api/companies?page=1&limit=5000&compact=1`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        if (!response.ok) {
          throw new Error(`Failed to load companies (${response.status})`);
        }

        const data = await response.json().catch(() => ({}));
        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.rows)
            ? data.rows
            : Array.isArray(data?.companies)
              ? data.companies
              : Array.isArray(data?.data?.rows)
                ? data.data.rows
                : Array.isArray(data?.data?.companies)
                  ? data.data.companies
                  : [];
        const list = rows
          .map((row) => buildCompanyOption(row))
          .filter(Boolean)
          .sort((a, b) => a.company_name.localeCompare(b.company_name));

        setCompanies(list);
      } catch (err) {
        console.error("Failed to load companies:", err);
        setCompanies([]);
        setCompanyLookupError("Failed to load companies.");
      } finally {
        setLoadingCompanies(false);
      }
    };

    loadCompanies();
  }, [apiBase, open]);

  const fetchTeamMembersList = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setTeamMembers([]);
      setTeamMembersDebug("No auth token");
      return;
    }

    setLoadingTeamMembers(true);
    setTeamMembersDebug("");

    try {
      // The roster includes recruiters, administrators, and saved team
      // members. Company contacts are not valid lead assignees.
      const rows = await fetchTeamRoster({ apiBase, token });
      setTeamMembers(rows);
      setTeamMembersDebug(rows.length ? "" : "No team members found");
    } catch (error) {
      console.error("Failed to load team members:", error);
      setTeamMembers([]);
      setTeamMembersDebug(error?.message || "Failed to load team members.");
    } finally {
      setLoadingTeamMembers(false);
    }
  }, [apiBase]);

  useEffect(() => {
    if (!open) return;
    fetchTeamMembersList();
  }, [fetchTeamMembersList, open]);

  const selectedCompanyId = useMemo(() => {
    const value = Number(local.companyId ?? companyOption?.company_id ?? 0);
    return Number.isFinite(value) && value > 0 ? value : null;
  }, [companyOption?.company_id, local.companyId]);
  const effectiveCompanyName = useMemo(
    () =>
      normalizeCompanyName(
        local.companyName || local.company || companyOption?.company_name || companyInputValue || ""
      ),
    [companyInputValue, companyOption?.company_name, local.company, local.companyName]
  );

  useEffect(() => {
    if (!open || !selectedCompanyId) {
      setCompanyJobs([]);
      setLoadingCompanyJobs(false);
      return undefined;
    }

    const controller = new AbortController();
    setLoadingCompanyJobs(true);

    fetch(
      `${apiBase}/api/jobs?page=1&limit=100&sort=created_at&order=desc&companyId=${encodeURIComponent(
        String(selectedCompanyId)
      )}`,
      {
        headers: getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : undefined,
        credentials: "include",
        signal: controller.signal,
      }
    )
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload?.error || "Failed to load company jobs.");
        return Array.isArray(payload?.rows) ? payload.rows : [];
      })
      .then((rows) => {
        setCompanyJobs(
          rows
            .map((row) => ({ id: row?.id ?? null, title: String(row?.job_title || "").trim() }))
            .filter((row) => row.title)
        );
      })
      .catch((error) => {
        if (error?.name !== "AbortError") setCompanyJobs([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingCompanyJobs(false);
      });

    return () => controller.abort();
  }, [apiBase, isContactMode, open, selectedCompanyId]);

  useEffect(() => {
    if (!open) return;

    const normalizedName = normalizeCompanyName(local.companyName);
    if (companyInputValue !== normalizedName) {
      setCompanyInputValue(normalizedName);
    }

    const matchedCompany = findMatchingCompanyOption(companies, {
      companyName: normalizedName,
      website: local.website,
      strict: true,
    });

    setCompanyOption((prev) => {
      if (!matchedCompany) {
        return null;
      }
      if (
        prev?.company_id === matchedCompany.company_id &&
        prev?.company_name === matchedCompany.company_name &&
        prev?.company_logo === matchedCompany.company_logo &&
        prev?.website === matchedCompany.website
      ) {
        return prev;
      }
      return matchedCompany;
    });
  }, [companies, companyInputValue, local.companyName, local.website, open]);

  useEffect(() => {
    if (!open || !companyOption) return;

    setLocal((prev) => {
      const nextCompanyId =
        prev.companyId != null ? prev.companyId : companyOption.company_id ?? null;
      const nextCompanyLogo = prev.companyLogo || companyOption.company_logo || "";
      const shouldFillWebsite =
        !String(prev.website || "").trim() && String(companyOption.website || "").trim();

      if (
        String(prev.companyId ?? "") === String(nextCompanyId ?? "") &&
        String(prev.companyLogo || "") === String(nextCompanyLogo || "") &&
        !shouldFillWebsite
      ) {
        return prev;
      }

      const next = {
        ...prev,
        companyId: nextCompanyId,
        companyLogo: nextCompanyLogo,
        ...(shouldFillWebsite ? { website: companyOption.website } : {}),
      };
      onChange?.(next);
      return next;
    });
  }, [companyOption, onChange, open]);

  const pushChange = useCallback(
    (patch) => {
      setFieldErrors((prev) => {
        const erroredField = Object.keys(patch || {}).find((key) => prev[key]);
        if (!erroredField) return prev;
        const next = { ...prev };
        delete next[erroredField];
        return next;
      });
      setLocal((prev) => {
        const next = { ...prev, ...patch };
        onChange?.(next);
        return next;
      });
    },
    [onChange]
  );

  useEffect(() => {
    if (!open || String(local.teamMember || "").trim()) return;

    const currentUserName = getCurrentUserName();
    if (currentUserName) pushChange({ teamMember: currentUserName });
  }, [local.teamMember, open, pushChange]);

  const setField = (key) => (e) => {
    const v = e.target.value;
    setErrorMsg("");
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    pushChange({ [key]: v });
  };

  const applyPlatformContact = useCallback(
    (contact) => {
      if (!contact || typeof contact === "string") return;

      // The menu shows a contact's complete name so duplicates can be
      // distinguished. Selecting it splits that name into the two matching
      // inputs instead of placing the complete name in one field.
      pushChange({
        firstName: getContactNamePart(contact, "first"),
        lastName: getContactNamePart(contact, "last"),
      });
    },
    [pushChange]
  );

  useEffect(() => {
    const query = [local.firstName, local.lastName].filter(Boolean).join(" ").trim();
    if (!open || query.length < 2) {
      setPlatformContactOptions([]);
      setLoadingPlatformContacts(false);
      return undefined;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => {
      setLoadingPlatformContacts(true);
      searchPlatformContacts({ apiBase, search: query, signal: controller.signal })
        .then((rows) => setPlatformContactOptions(rows))
        .catch((error) => {
          if (error?.name !== "AbortError") setPlatformContactOptions([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoadingPlatformContacts(false);
        });
    }, 75);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [apiBase, local.firstName, local.lastName, open]);

  const filterCompanyOptions = useCallback((options, params) => {
    const inputValue = normalizeCompanyName(params.inputValue);
    const normalizedInput = normalizeLookupValue(inputValue);
    const filtered = options.filter((option) => {
      if (!normalizedInput) return true;
      return normalizeLookupValue(option?.company_name || "").includes(normalizedInput);
    });
    const base = companyFilter(filtered, {
      ...params,
      inputValue: "",
    });
    const exists = options.some(
      (option) => normalizeLookupValue(option?.company_name || "") === normalizedInput
    );

    if (inputValue && !exists) {
      base.push({
        inputValue,
        company_id: null,
        company_name: inputValue,
        company_logo: "",
        website: "",
      });
    }

    return base;
  }, []);

  const handleCompanyInputChange = useCallback(
    (_, newInputValue, reason) => {
      if (reason === "reset") return;

      const normalizedInput = normalizeCompanyName(newInputValue);
      setCompanyInputValue(normalizedInput);

      if (!normalizedInput) {
        setCompanyOption(null);
        pushChange({
          companyName: "",
          companyId: null,
          companyLogo: "",
        });
        return;
      }

      const exact = companies.find(
        (option) =>
          normalizeLookupValue(option?.company_name || "") === normalizeLookupValue(normalizedInput)
      );

      if (exact) {
        setCompanyOption(exact);
        const isSameCompany =
          normalizeLookupValue(local.companyName) === normalizeLookupValue(exact.company_name);
        pushChange({
          companyName: exact.company_name,
          companyId: exact.company_id ?? null,
          companyLogo: exact.company_logo || "",
          website: exact.website || (isSameCompany ? local.website || "" : ""),
        });
        return;
      }

      setCompanyOption(null);
      pushChange({
        companyName: normalizedInput,
        companyId: null,
        companyLogo: "",
      });
    },
    [companies, local.companyName, local.website, pushChange]
  );

  const handleCompanyChange = useCallback(
    (_, newValue) => {
      if (typeof newValue === "string") {
        const normalizedInput = normalizeCompanyName(newValue);
        setCompanyInputValue(normalizedInput);
        setCompanyOption(null);
        pushChange({
          companyName: normalizedInput,
          companyId: null,
          companyLogo: "",
        });
        return;
      }

      if (newValue?.inputValue) {
        const normalizedInput = normalizeCompanyName(newValue.inputValue);
        setCompanyInputValue(normalizedInput);
        setCompanyOption(null);
        pushChange({
          companyName: normalizedInput,
          companyId: null,
          companyLogo: "",
        });
        return;
      }

      const nextOption = buildCompanyOption(newValue);
      setCompanyOption(nextOption);
      setCompanyInputValue(nextOption?.company_name || "");

      const isSameCompany =
        normalizeLookupValue(local.companyName) === normalizeLookupValue(nextOption?.company_name || "");

      pushChange({
        companyName: nextOption?.company_name || "",
        companyId: nextOption?.company_id ?? null,
        companyLogo: nextOption?.company_logo || "",
        website: nextOption?.website || (isSameCompany ? local.website || "" : ""),
      });
    },
    [local.companyName, local.website, pushChange]
  );

  const summaryLine = useMemo(() => {
    const contactName = [local.firstName?.trim(), local.lastName?.trim()].filter(Boolean).join(" ");
    const bits = isContactMode
      ? [contactName, local.companyName?.trim(), local.location?.trim()].filter(Boolean)
      : [local.companyName?.trim(), local.location?.trim(), local.jobTitle?.trim()].filter(Boolean);
    return bits.length ? `Info: ${bits.join(", ")}` : "Info:";
  }, [isContactMode, local.companyName, local.firstName, local.jobTitle, local.lastName, local.location]);

  const assignTeamOptions = teamMembers;
  const assignTeamLoading = loadingTeamMembers;
  const assignTeamDebug = teamMembersDebug;

  const selectedTeamMember = useMemo(() => {
    const normalizedValue = normalizeTeamMemberName(local.teamMember).toLowerCase();
    if (!normalizedValue) return null;
    return (
      assignTeamOptions.find(
        (option) => normalizeTeamMemberName(option?.name).toLowerCase() === normalizedValue
      ) || null
    );
  }, [assignTeamOptions, local.teamMember]);

  const leadLinkSummary = useMemo(() => {
    const summary = describeLinkAutofill(deriveLinkAutofill(local.leadLink, isContactMode ? "contact" : "lead"), {
      includeProfile: true,
      includeWebsite: true,
    });
    return summary || "Autofills source and company when detected.";
  }, [isContactMode, local.leadLink]);

  const handleLeadLinkChange = useCallback(
    (e) => {
      const nextValue = e?.target?.value || "";
      const autofill = deriveLinkAutofill(nextValue, isContactMode ? "contact" : "lead");
      const nextPatch = {
        leadLink: nextValue,
      };

      if (autofill.source) nextPatch.source = autofill.source;

      if (String(autofill.companyName || "").trim().length > 1) {
        const matchedCompany = findMatchingCompanyOption(companies, {
          companyName: autofill.companyName,
          website: autofill.website || "",
        });

        if (matchedCompany) {
          setCompanyOption(matchedCompany);
          setCompanyInputValue(matchedCompany.company_name || autofill.companyName);
          nextPatch.companyName = matchedCompany.company_name || autofill.companyName;
          nextPatch.companyId = matchedCompany.company_id ?? null;
          nextPatch.companyLogo = matchedCompany.company_logo || "";
          nextPatch.website = matchedCompany.website || autofill.website || "";
        } else {
          setCompanyOption(null);
          setCompanyInputValue(normalizeCompanyName(autofill.companyName));
          nextPatch.companyName = autofill.companyName;
          nextPatch.companyId = null;
          nextPatch.companyLogo = "";
          if (autofill.website) nextPatch.website = autofill.website;
        }
      } else if (autofill.website) {
        nextPatch.website = autofill.website;
      }

      if (autofill.source === "LinkedIn" && autofill.normalizedUrl) nextPatch.linkedin = autofill.normalizedUrl;

      const profileName = String(autofill.profileName || "").trim();
      if (profileName && !String(local.firstName || local.lastName || "").trim()) {
        const parts = profileName.split(" ").filter(Boolean);
        nextPatch.firstName = parts[0] || "";
        nextPatch.lastName = parts.slice(1).join(" ");
      }

      pushChange(nextPatch);
      setErrorMsg("");
    },
    [companies, isContactMode, local.firstName, local.lastName, pushChange]
  );

  const handleInlineLinkContinue = async () => {
    const link = String(local.leadLink || "").trim();
    if (!link) return;

    if (isLinkedInProfileUrl(link)) {
      setContactFromProfileLink(true);
      const token = getAuthToken();
      setSubmitting(true);
      setErrorMsg("");
      try {
        if (!token) throw new Error("Please log in to read this profile automatically.");
        const response = await fetch(`${apiBase}/api/candidates/parse-link`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
          },
          body: JSON.stringify({ url: safeUrl(link) || link }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.error || "Unable to read this profile.");
        const parsed = data?.parsed || {};
        const patch = {};
        const name = String(parsed.full_name || "").trim().split(/\s+/).filter(Boolean);
        if (name.length) {
          patch.firstName = name[0];
          patch.lastName = name.slice(1).join(" ");
        }
        for (const [field, parsedValue] of Object.entries({
          contactTitle: parsed.title,
          email: parsed.email,
          phoneNumber: parsed.phone,
          location: parsed.location,
          linkedin: parsed.linkedin_url,
          website: parsed.company_website,
        })) {
          if (String(parsedValue || "").trim()) patch[field] = String(parsedValue).trim();
        }
        const companyName = String(parsed.company_client || parsed.current_company || "").trim();
        if (companyName) {
          const match = findMatchingCompanyOption(companies, {
            companyName, website: parsed.company_website || "",
          });
          patch.companyName = match?.company_name || companyName;
          patch.companyId = match?.company_id ?? null;
          patch.companyLogo = match?.company_logo || "";
          patch.website = match?.website || parsed.company_website || local.website || "";
          setCompanyOption(match || null);
          setCompanyInputValue(patch.companyName);
        }
        pushChange(patch);
        const warning = String(data.warning || parsed.parse_warning || "").trim();
        if (warning) setErrorMsg(warning.replace(/candidate/gi, "contact"));
      } catch (error) {
        setErrorMsg(`${error?.message || "Unable to read this profile."} You can complete the contact details manually.`);
      } finally {
        setSubmitting(false);
        setScreen("overview");
      }
      return;
    }

    // Leads created from a job-posting link must retain the role name from
    // that posting. Previously this flow only inferred the company/source
    // from the URL, leaving jobTitle blank and rendering as "Lead {id}".
    if (!isContactMode && !String(local.jobTitle || "").trim()) {
      const token = getAuthToken();
      if (!token) {
        // Link parsing is a convenience only. The manual review fields must
        // remain available even if no parser session is available.
        setErrorMsg("We couldn't read this link automatically. Select the company and job manually.");
        setScreen("overview");
        return;
      }

      setSubmitting(true);
      setErrorMsg("");
      try {
        const response = await fetch(`${apiBase}/api/jobs/parse-link`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
          },
          body: JSON.stringify({
            url: link,
            company_name: effectiveCompanyName || "",
            company_website: local.website || "",
            source: local.source || "",
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.error || "Unable to read the job title from this link.");
        }

        const parsed = data?.parsed || {};
        const parsedJobTitle = String(parsed.job_title || parsed.title || "").trim();
        if (!parsedJobTitle || /^(lead|job)(?:\s*(?:#|no\.?)?\s*\d*)?$/i.test(parsedJobTitle)) {
          throw new Error("This link did not provide a job title. Please use a direct job posting link.");
        }

        const nextPatch = {
          jobTitle: parsedJobTitle,
          source: String(parsed.source || local.source || "").trim(),
          location: String(parsed.location || local.location || "").trim(),
          website: String(parsed.website || local.website || "").trim(),
          jobOpenings:
            parsed.job_openings != null && String(parsed.job_openings).trim()
              ? String(parsed.job_openings).trim()
              : local.jobOpenings || "",
          notes: String(parsed.description || local.notes || "").trim(),
        };
        if (!effectiveCompanyName && String(parsed.company_name || "").trim()) {
          const parsedCompanyName = String(parsed.company_name).trim();
          const matchingCompany = findMatchingCompanyOption(companies, {
            companyName: parsedCompanyName,
            website: parsed.website || "",
          });
          nextPatch.companyName = matchingCompany?.company_name || parsedCompanyName;
          nextPatch.companyId = matchingCompany?.company_id ?? null;
          nextPatch.companyLogo = matchingCompany?.company_logo || parsed.company_logo_url || "";
          if (matchingCompany) {
            setCompanyOption(matchingCompany);
            setCompanyInputValue(matchingCompany.company_name || parsedCompanyName);
          } else {
            setCompanyInputValue(parsedCompanyName);
          }
        }
        pushChange(nextPatch);
        setScreen("overview");
      } catch (error) {
        // Do not block manual lead creation on URL parsing. The next screen
        // lets users review or choose Company, Job, Source, Status, and Team
        // Member without relying on any AI automation.
        setErrorMsg(
          `${error?.message || "Unable to read the job title from this link."} You can select the company and job manually.`
        );
        setScreen("overview");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setScreen("overview");
  };

  const validateOverview = () => {
    if (readOnly) return true;
    const nextFieldErrors = {};
    const isJobLinkFlow = !isContactMode && Boolean(String(local.leadLink || "").trim()) &&
      !isLinkedInProfileUrl(local.leadLink);

    if (!effectiveCompanyName) {
      nextFieldErrors.companyName = "Company Name is required.";
    }

    if (!String(local.source || "").trim()) {
      nextFieldErrors.source = "Source is required.";
    }

    // A job-posting URL can create a lead before a person has been identified.
    // Keep contact names required for the normal/manual lead form and contact
    // form, but let the Link flow be completed from its Company and Job data.
    if (!isJobLinkFlow) {
      if (!String(local.firstName || "").trim()) {
        nextFieldErrors.firstName = "First Name is required.";
      }

      if (!String(local.lastName || "").trim()) {
        nextFieldErrors.lastName = "Last Name is required.";
      }
    }

    if (!isContactMode && !String(local.jobTitle || "").trim()) {
      nextFieldErrors.jobTitle = isJobLinkFlow
        ? "Select or enter the job for this URL."
        : "Job Title is required.";
    }

    if (isContactMode && String(local.jobTitle || "").trim() && !local.jobId) {
      nextFieldErrors.jobTitle = "Select an existing company job to attach this contact.";
    }

    if ((isContactMode || contactFromProfileLink) && !selectedCompanyId) {
      nextFieldErrors.companyName = "Select an existing company to add a company contact.";
    }

    if (Object.keys(nextFieldErrors).length) {
      setFieldErrors(nextFieldErrors);
      setErrorMsg("");
      return false;
    }

    setFieldErrors({});
    setErrorMsg("");
    return true;
  };

  const validateAll = () => {
    if (!validateOverview()) return false;

    const websiteOk = safeUrl(local.website);
    if (String(local.website || "").trim() && websiteOk === null) {
      setErrorMsg("Enter a valid website URL.");
      return false;
    }

    if (
      String(local.jobOpenings || "").trim() &&
      (!Number.isFinite(Number(local.jobOpenings)) || Number(local.jobOpenings) < 0)
    ) {
      setErrorMsg("Job openings must be a valid non-negative number.");
      return false;
    }

    const linkOk = safeUrl(local.leadLink);
    if (String(local.leadLink || "").trim() && linkOk === null) {
      setErrorMsg("Enter a valid link URL.");
      return false;
    }

    const liOk = safeUrl(local.linkedin);
    if (String(local.linkedin || "").trim() && liOk === null) {
      setErrorMsg("Enter a valid LinkedIn URL.");
      return false;
    }

    const otherOk = safeUrl(local.otherLink);
    if (String(local.otherLink || "").trim() && otherOk === null) {
      setErrorMsg("Enter a valid Other Link URL.");
      return false;
    }

    setErrorMsg("");
    return true;
  };

  const handleClose = () => {
    if (!submitting) onClose?.();
  };

  const ensureTeamMemberValue = useCallback(
    async (value, token) => {
      const normalizedValue = normalizeTeamMemberName(value);
      if (!normalizedValue) return "";

      const ensuredTeamMember = await createTeamMemberIfMissing({
        apiBase,
        token,
        name: normalizedValue,
        existing: teamMembers,
      });
      const finalValue = ensuredTeamMember?.name || normalizedValue;
      setTeamMembers((prev) =>
        upsertTeamMemberOption(prev, ensuredTeamMember || { name: finalValue })
      );
      return finalValue;
    },
    [apiBase, teamMembers]
  );

  const navigateToCreatedLead = useCallback(
    (createdLead) => {
      if (isContactMode) return;

      const leadId = createdLead?.id ?? createdLead?.lead_id ?? null;
      if (!leadId) return;

      navigate(
        {
          pathname: "/leads",
          search: `?${new URLSearchParams({
            leadId: String(leadId),
            tab: "details",
          }).toString()}`,
        },
        { replace: false }
      );
    },
    [isContactMode, navigate]
  );

  const handleSubmitLead = async () => {
    setErrorMsg("");
    if (readOnly) return;
    if (!validateAll()) return;

    try {
      setSubmitting(true);

      const token = getAuthToken();
      if (!token) throw new Error("No auth token found. Please log in again.");

      let finalTeamMember = normalizeTeamMemberName(local.teamMember);
      if (finalTeamMember) {
        finalTeamMember = await ensureTeamMemberValue(finalTeamMember, token);
        pushChange({ teamMember: finalTeamMember });
      }

      const normalizedWebsite = safeUrl(local.website) || local.website || "";
      const normalizedLeadLink = isLinkedInProfileUrl(local.leadLink)
        ? "" : safeUrl(local.leadLink) || local.leadLink || "";
      const normalizedLinkedin = safeUrl(local.linkedin) || local.linkedin || "";
      const normalizedOtherLink = safeUrl(local.otherLink) || local.otherLink || "";
      const normalizedContactName = `${local.firstName || ""} ${local.lastName || ""}`.trim();

      const submittedLeadPayload = {
        id: isEditingExisting ? editingLeadId : null,
        lead_id: isEditingExisting ? editingLeadId : null,
        companyName: effectiveCompanyName,
        company_name: effectiveCompanyName,
        // Keep the display-form name and every API compatibility alias in
        // sync. The header quick action opens this dialog without a parent
        // form, so it must not depend on a caller to provide one variant.
        company: effectiveCompanyName,
        name: effectiveCompanyName,
        companyId: selectedCompanyId ?? local.companyId ?? null,
        company_id: selectedCompanyId ?? local.companyId ?? undefined,
        companyLogo: local.companyLogo || "",
        company_logo: local.companyLogo || "",
        website: normalizedWebsite,
        company_website: normalizedWebsite,
        source: local.source || "",
        contactName: normalizedContactName,
        contact_name: normalizedContactName,
        contact_title: local.contactTitle || "",
        job_id: local.jobId || null,
        save_company_contact: Boolean(normalizedContactName),
        email: local.email || "",
        contact_email: local.email || "",
        phone: local.phoneNumber || "",
        phoneNumber: local.phoneNumber || "",
        contact_phone: local.phoneNumber || "",
        location: local.location || "",
        city: local.location || "",
        linkedin: normalizedLinkedin,
        linkedin_url: normalizedLinkedin,
        leadStatus: local.leadStatus || "",
        status: local.leadStatus || "",
        teamMember: finalTeamMember || "",
        team_member: finalTeamMember || "",
        owner_name: finalTeamMember || "",
        job_openings: local.jobOpenings || "",
        jobOpenings: local.jobOpenings || "",
        aiSummary: local.notes || "",
        full_company_description: local.notes || "",
        notes: local.notes || "",
        jobTitle: local.jobTitle || "",
        job_title: local.jobTitle || "",
        otherLink: normalizedOtherLink,
        other_link: normalizedOtherLink,
        leadLink: normalizedLeadLink,
        lead_link: normalizedLeadLink,
      };

      let submittedLead = submittedLeadPayload;
      let shouldNavigateToCreatedLead = !isContactMode && !isEditingExisting;

      if (isContactMode) {
        const resp = await fetch(`${apiBase}/api/hiring-managers`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
          },
          body: JSON.stringify({
            entity_type: "company",
            entity_id: selectedCompanyId,
            job_id: local.jobId || null,
            name: normalizedContactName,
            title: local.contactTitle || "",
            email: local.email || "",
            phone: local.phoneNumber || "",
            linkedin_url: normalizedLinkedin,
            source: local.source || "Manual entry",
            contact_kind: "contact",
            location: local.location || "",
            owner: finalTeamMember || "",
            status: local.leadStatus || "New",
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.error || "Failed to create contact.");
        }
        submittedLead = data?.hiringManager || data;
      } else if (isEditingExisting) {
        const resp = await fetch(`${apiBase}/api/leads/${editingLeadId}/info`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
          },
          body: JSON.stringify(submittedLeadPayload),
        });

        const contentType = resp.headers.get("content-type") || "";
        const data = contentType.includes("application/json")
          ? await resp.json().catch(() => ({}))
          : { error: await resp.text().catch(() => "") };

        if (!resp.ok) {
          throw new Error(data?.error || `Failed to update ${entityLabel.toLowerCase()}.`);
        }

        submittedLead = {
          ...submittedLeadPayload,
          ...(data?.lead || data || {}),
          id: editingLeadId,
          lead_id: editingLeadId,
        };
      } else {
        const resp = await fetch(`${apiBase}/api/leads`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
          },
          // The API uses express.json() for lead creation. Sending multipart
          // FormData left req.body empty, including the selected company name.
          body: JSON.stringify(submittedLeadPayload),
        });

        const contentType = resp.headers.get("content-type") || "";
        const data = contentType.includes("application/json")
          ? await resp.json().catch(() => ({}))
          : { error: await resp.text().catch(() => "") };

        if (!resp.ok) {
          throw new Error(data?.error || `Failed to submit ${entityLabel.toLowerCase()}.`);
        }

        submittedLead = data?.lead || data;
      }

      if (typeof onSubmit === "function") {
        try {
          const callbackResult = onSubmit(local, submittedLead);
          const resolvedCallbackResult =
            callbackResult && typeof callbackResult.then === "function"
              ? await callbackResult
              : callbackResult;

          if (resolvedCallbackResult === false) {
            shouldNavigateToCreatedLead = false;
          }
        } catch (callbackError) {
          console.error("LeadDescriptionDialog onSubmit callback failed:", callbackError);
        }
      }

      setToast({
        open: true,
        type: "success",
        msg: `${entityLabel} ${isEditingExisting ? "updated" : "created"}`,
      });
      onClose?.();

      if (shouldNavigateToCreatedLead && !contactFromProfileLink) {
        navigateToCreatedLead(submittedLead);
      } else if (isContactMode || contactFromProfileLink) {
        if (contactFromProfileLink && typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("company-contact-created", {
            detail: { companyId: selectedCompanyId, contact: submittedLead },
          }));
        }
        navigate({
          pathname: "/companies",
          search: `?${new URLSearchParams({
            companyId: String(selectedCompanyId), tab: "contacts", entityType: "company",
          }).toString()}`,
        });
      }
    } catch (err) {
      console.error(`${entityLabel} submit failed:`, err);
      setErrorMsg(err?.message || `Failed to submit ${entityLabel.toLowerCase()}.`);
    } finally {
      setSubmitting(false);
    }
  };

  /* =========================
   * Screens
   * ========================= */
  const renderStart = () => (
    <CardShell title={baseDialogTitle} onClose={handleClose} disabled={submitting}>
      <Box sx={{ p: 4 }}>
        <Stack spacing={1.25}>
          <ChoiceRow
            icon={<BusinessOutlinedIcon sx={{ fontSize: 18, color: PURPLE }} />}
            title="Start from Scratch"
            subtitle={`Manually enter ${entityLabel.toLowerCase()} information`}
            onClick={() => setScreen("overview")}
          />

          <InlineLinkEntryCard
            title="Use Link"
            subtitle="Paste a LinkedIn link, company page, or post URL"
            value={local.leadLink}
            onChange={handleLeadLinkChange}
            onSubmit={handleInlineLinkContinue}
            placeholder="https://www.linkedin.com/company/... or https://company.com"
            helperText={leadLinkSummary}
            loading={submitting}
            disabled={submitting}
          />

        </Stack>
      </Box>
    </CardShell>
  );

  const renderOverview = () => (
    <CardShell title={baseDialogTitle} onClose={handleClose} disabled={submitting}>
      <Box sx={{ px: 4, pt: 2.5, pb: 1.5 }}>
        <Section>{isContactMode ? "Contact Overview" : "Lead Overview"}</Section>
      </Box>

      <Box sx={{ px: 4, pb: 4 }}>
        <Stack spacing={1.25}>
          {!!errorMsg && <Alert severity="error">{errorMsg}</Alert>}

          <Box>
            <LabelText>Company Name</LabelText>
            <Autocomplete
              freeSolo
              options={companies}
              loading={loadingCompanies}
              value={companyOption}
              inputValue={companyInputValue}
              onInputChange={handleCompanyInputChange}
              onChange={handleCompanyChange}
              filterOptions={filterCompanyOptions}
              getOptionLabel={getCompanyOptionLabel}
              isOptionEqualToValue={(option, selectedValue) =>
                option.company_id === selectedValue.company_id &&
                option.company_name === selectedValue.company_name
              }
              noOptionsText={loadingCompanies ? "Loading..." : companyLookupError || "No companies found"}
              disabled={readOnly}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}
                >
                  {option?.inputValue ? (
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {`Use "${option.inputValue}"`}
                    </Typography>
                  ) : (
                    <>
                      <Avatar src={option.company_logo || undefined} sx={{ width: 28, height: 28 }}>
                        {option.company_name?.[0]}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#111827" }}>
                          <CompanyOptionLabel company={option} />
                        </Typography>
                        {!!option.website && (
                          <Typography variant="caption" sx={{ color: "#6B7280" }}>
                            {option.website}
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  fullWidth
                  placeholder="Search or enter company name"
                  error={Boolean(fieldErrors.companyName)}
                  helperText={fieldErrors.companyName || ""}
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment:
                      companyOption?.company_logo || local.companyLogo ? (
                        <>
                          <Avatar
                            src={companyOption?.company_logo || local.companyLogo || undefined}
                            sx={{ width: 24, height: 24, mr: 1 }}
                          >
                            {(companyOption?.company_name || local.companyName || "")?.[0]}
                          </Avatar>
                          {params.InputProps.startAdornment}
                        </>
                      ) : (
                        params.InputProps.startAdornment
                      ),
                    endAdornment: (
                      <>
                        {loadingCompanies ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          <Box>
            <LabelText>Company Website (Optional)</LabelText>
            <TextField
              value={local.website}
              onChange={setField("website")}
              size="small"
              fullWidth
              placeholder="https://company.com"
              disabled={readOnly}
              sx={fieldSx}
            />
          </Box>

          <Box>
            <LabelText>Source</LabelText>
            <TextField
              select
              value={local.source}
              onChange={setField("source")}
              size="small"
              fullWidth
              disabled={readOnly}
              error={Boolean(fieldErrors.source)}
              helperText={fieldErrors.source || ""}
              sx={fieldSx}
            >
              {SOURCE_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <LabelText>Contact</LabelText>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <Autocomplete
                freeSolo
                sx={{ flex: 1, minWidth: 0 }}
                options={platformContactOptions}
                filterOptions={(options) => options}
                loading={loadingPlatformContacts}
                slotProps={{
                  popper: {
                    placement: "bottom-start",
                    modifiers: [{ name: "flip", enabled: false }],
                  },
                }}
                inputValue={local.firstName}
                onInputChange={(_, value, reason) => {
                  if (reason !== "reset") pushChange({ firstName: value || "" });
                }}
                onChange={(_, option) => applyPlatformContact(option)}
                getOptionLabel={getContactFullName}
                noOptionsText={loadingPlatformContacts ? "Searching contacts..." : "No contacts found"}
                disabled={readOnly}
                renderOption={(props, option) => <Box component="li" {...props}><Box><Typography sx={{ fontSize: 13, fontWeight: 700 }}>{getContactFullName(option) || "Contact"}</Typography>{option.email || option.role ? <Typography sx={{ fontSize: 11, color: LABEL }}>{[option.role, option.email].filter(Boolean).join(" | ")}</Typography> : null}</Box></Box>}
                renderInput={(params) => <TextField {...params} size="small" fullWidth placeholder="First Name" autoComplete="new-password" inputProps={{ ...params.inputProps, name: "contact-first-name-search", autoComplete: "new-password" }} error={Boolean(fieldErrors.firstName)} helperText={fieldErrors.firstName || ""} sx={fieldSx} InputProps={{ ...params.InputProps, startAdornment: <><InputAdornment position="start"><PersonOutlineIcon sx={{ fontSize: 18, color: PURPLE }} /></InputAdornment>{params.InputProps.startAdornment}</>, endAdornment: <>{loadingPlatformContacts ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }} />}
              />
              <Autocomplete
                freeSolo
                sx={{ flex: 1, minWidth: 0 }}
                options={platformContactOptions}
                filterOptions={(options) => options}
                loading={loadingPlatformContacts}
                slotProps={{
                  popper: {
                    placement: "bottom-start",
                    modifiers: [{ name: "flip", enabled: false }],
                  },
                }}
                inputValue={local.lastName}
                onInputChange={(_, value, reason) => {
                  if (reason !== "reset") pushChange({ lastName: value || "" });
                }}
                onChange={(_, option) => applyPlatformContact(option)}
                getOptionLabel={getContactFullName}
                noOptionsText={loadingPlatformContacts ? "Searching contacts..." : "No contacts found"}
                disabled={readOnly}
                renderOption={(props, option) => <Box component="li" {...props}><Box><Typography sx={{ fontSize: 13, fontWeight: 700 }}>{getContactFullName(option) || "Contact"}</Typography>{option.email || option.role ? <Typography sx={{ fontSize: 11, color: LABEL }}>{[option.role, option.email].filter(Boolean).join(" | ")}</Typography> : null}</Box></Box>}
                renderInput={(params) => <TextField {...params} size="small" fullWidth placeholder="Last Name" autoComplete="new-password" inputProps={{ ...params.inputProps, name: "contact-last-name-search", autoComplete: "new-password" }} error={Boolean(fieldErrors.lastName)} helperText={fieldErrors.lastName || ""} sx={fieldSx} InputProps={{ ...params.InputProps, endAdornment: <>{loadingPlatformContacts ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }} />}
              />
            </Stack>
          </Box>

          <Box>
            <LabelText>Email (Optional)</LabelText>
            <TextField
              value={local.email}
              onChange={setField("email")}
              size="small"
              fullWidth
              placeholder="email@company.com"
              disabled={readOnly}
              sx={fieldSx}
            />
          </Box>

          <Box>
            <LabelText>Phone (Optional)</LabelText>
            <TextField
              value={local.phoneNumber}
              onChange={setField("phoneNumber")}
              size="small"
              fullWidth
              placeholder="+1 (555) 555-5555"
              disabled={readOnly}
              sx={fieldSx}
            />
          </Box>

          <Box>
            <LabelText>{isContactMode ? "Job (Optional)" : "Job"}</LabelText>
              <Autocomplete
                freeSolo={!isContactMode}
                options={companyJobs}
                loading={loadingCompanyJobs}
                inputValue={local.jobTitle || ""}
                onInputChange={(_, value, reason) => {
                  if (reason !== "reset") pushChange({ jobTitle: value || "", jobId: null });
                }}
                onChange={(_, option) => {
                  pushChange({
                    jobTitle: typeof option === "string" ? option : option?.title || "",
                    jobId: typeof option === "string" ? null : option?.id ?? null,
                  });
                }}
                getOptionLabel={(option) => (typeof option === "string" ? option : option?.title || "")}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                disabled={readOnly}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{option?.title || ""}</Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    fullWidth
                    placeholder="Select a company job or enter a job title"
                    helperText={
                      fieldErrors.jobTitle || (selectedCompanyId
                        ? "Select an existing job or enter the detected job title manually."
                        : "Select a company first, then choose or enter a job title.")
                    }
                    error={Boolean(fieldErrors.jobTitle)}
                    sx={fieldSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCompanyJobs ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
          </Box>

          {(isContactMode || contactFromProfileLink) && (
            <Box>
              <LabelText>Contact Title (Optional)</LabelText>
              <TextField value={local.contactTitle} onChange={setField("contactTitle")}
                size="small" fullWidth placeholder="Head of Talent" disabled={readOnly} sx={fieldSx} />
            </Box>
          )}

          {!isContactMode && (
            <Box>
              <LabelText>Job Openings (Optional)</LabelText>
              <TextField
                value={local.jobOpenings}
                onChange={setField("jobOpenings")}
                size="small"
                fullWidth
                type="number"
                placeholder="1"
                disabled={readOnly}
                sx={fieldSx}
              />
            </Box>
          )}

          <Box>
            <LabelText>Location (city / country) (Optional)</LabelText>
            <LocationSearchableField
              value={local.location}
              onChange={(nextValue) => setLocal((prev) => ({ ...prev, location: nextValue }))}
              placeholder="New York, USA"
              textFieldProps={{ disabled: readOnly, sx: fieldSx }}
            />
          </Box>
        </Stack>
      </Box>

      <FooterBar
        disabled={submitting}
        leftText={isContactMode ? "Cancel" : "Back"}
        onLeft={isContactMode ? handleClose : () => setScreen("start")}
        rightText="Next"
        onRight={() => {
          if (!validateOverview()) return;
          setScreen("additional");
        }}
      />
    </CardShell>
  );

  const renderAdditional = () => (
    <CardShell title={baseDialogTitle} onClose={handleClose} disabled={submitting}>
      <Box sx={{ px: 4, pt: 2.5, pb: 1 }}>
        <Typography sx={{ fontSize: 11.5, color: "#6B7280", fontWeight: 700 }}>
          {summaryLine}
        </Typography>
      </Box>

      <Box sx={{ px: 4, pt: 1, pb: 1.5 }}>
        <Section>Additional Details</Section>
      </Box>

      <Box sx={{ px: 4, pb: 4 }}>
        <Stack spacing={1.25}>
          {!!errorMsg && <Alert severity="error">{errorMsg}</Alert>}

          <Box>
            <LabelText>LinkedIn Profile Link (Optional)</LabelText>
            <TextField
              value={local.linkedin}
              onChange={setField("linkedin")}
              size="small"
              fullWidth
              placeholder="https://www.linkedin.com/in/..."
              disabled={readOnly}
              sx={fieldSx}
            />
          </Box>

          <Box>
            <LabelText>Other Link (Optional)</LabelText>
            <TextField
              value={local.otherLink}
              onChange={setField("otherLink")}
              size="small"
              fullWidth
              placeholder="https://..."
              disabled={readOnly}
              sx={fieldSx}
            />
          </Box>

          <Box>
            <LabelText>Notes (optional)</LabelText>
            <TextField
              value={local.notes}
              onChange={setField("notes")}
              size="small"
              fullWidth
              multiline
              minRows={4}
              disabled={readOnly}
              sx={fieldSx}
            />
          </Box>

          <Box>
            <LabelText>Team Member</LabelText>
            <Autocomplete
              freeSolo
              openOnFocus
              selectOnFocus
              clearOnBlur={false}
              handleHomeEndKeys
              options={assignTeamOptions}
              loading={assignTeamLoading}
              value={selectedTeamMember}
              inputValue={local.teamMember || ""}
              onOpen={() => {
                if (!teamMembers.length && !loadingTeamMembers) fetchTeamMembersList();
              }}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === "reset") return;
                if (reason === "clear") {
                  pushChange({ teamMember: "" });
                  return;
                }
                pushChange({ teamMember: newInputValue || "" });
              }}
              onChange={(_, newValue) => {
                if (typeof newValue === "string") {
                  pushChange({ teamMember: newValue });
                  return;
                }
                pushChange({
                  teamMember: newValue?.inputValue || newValue?.name || "",
                });
              }}
              filterOptions={(options, params) => {
                const inputValue = normalizeTeamMemberName(params.inputValue).toLowerCase();
                const filtered = options.filter(
                  (option) => !inputValue || option.name.toLowerCase().includes(inputValue)
                );
                const base = dedupeAutocompleteOptions(teamMemberFilter(filtered, {
                  ...params,
                  inputValue: "",
                }));
                const exists = options.some(
                  (option) => option.name.toLowerCase() === inputValue
                );

                if (inputValue && !exists) {
                  base.push({
                    id: null,
                    inputValue: params.inputValue,
                    name: params.inputValue,
                  });
                }

                return dedupeAutocompleteOptions(base, (option) =>
                  normalizeAutocompleteText(option?.name || option?.inputValue || "")
                );
              }}
              getOptionLabel={(option) => {
                if (typeof option === "string") return option;
                if (option?.inputValue) return option.inputValue;
                return String(option?.name || "");
              }}
              isOptionEqualToValue={(option, value) => {
                const optionId = option?.id ?? null;
                const valueId = value?.id ?? null;

                if (optionId != null && valueId != null) {
                  return String(optionId) === String(valueId);
                }

                return (
                  normalizeTeamMemberName(option?.name || option?.inputValue || "").toLowerCase() ===
                  normalizeTeamMemberName(value?.name || value?.inputValue || "").toLowerCase()
                );
              }}
              disabled={readOnly}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  key={`${option?.id ?? "new"}-${option?.name ?? option?.inputValue ?? ""}`}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                    {option?.inputValue ? `Create "${option.inputValue}"` : option?.name || ""}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  fullWidth
                  placeholder={
                    "Search or select a team member"
                  }
                  helperText={
                    loadingTeamMembers
                      ? "Loading recruiters, administrators, and team members..."
                      : teamMembers.length
                        ? "The signed-in user is selected by default. Choose a recruiter, administrator, or team member."
                        : assignTeamDebug || "No team members found. Type a name to create one on save."
                  }
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {assignTeamLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          <Box>
            <LabelText>Status</LabelText>
            <TextField
              select
              value={local.leadStatus}
              onChange={setField("leadStatus")}
              size="small"
              fullWidth
              disabled={readOnly}
              sx={fieldSx}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Stack>
      </Box>

      <FooterBar
        disabled={submitting}
        leftText="Back"
        onLeft={() => setScreen("overview")}
        rightText={submitting ? "Saving..." : dialogSubmitText}
        onRight={handleSubmitLead}
      />
    </CardShell>
  );

  const current = () => {
    if (screen === "start") return renderStart();
    if (screen === "overview") return renderOverview();
    if (screen === "additional") return renderAdditional();
    return isContactMode ? renderOverview() : renderStart();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, bgcolor: "transparent", boxShadow: "none" },
        }}
      >
        <Box sx={{ p: 1, bgcolor: "transparent" }}>{current()}</Box>
        <input ref={fileInputRef} type="file" style={{ display: "none" }} />
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3200}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      >
        <Alert severity={toast.type} onClose={() => setToast((p) => ({ ...p, open: false }))}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
